import { useState } from "react";
import { useNavigate } from "@/lib/router-compat";
import { ContentPageNav } from "@/components/ContentPageNav";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  CheckCircle,
  ArrowRight,
  Building2,
  Users,
  Shuffle,
  Clock,
  Bell,
  Shield,
  Smartphone,
} from "lucide-react";
import { PRICING_TIERS, formatPrice, getExampleCostsPerMember, type PricingTier, type BillingInterval } from "@/lib/pricing";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { ClubCarousel } from "@/components/ClubCarousel";
import husselLogo from "@/assets/hussel-app-logo.png";
import oscarPhoto from "@/assets/oscar-photo.jpg";

const OverDeHusselApp = () => {
  const navigate = useNavigate();
  const [billingInterval, setBillingInterval] = useState<BillingInterval>('yearly');

  const benefits = [
    "Geen vrijwilligers meer nodig tijdens het spelen",
    "Geen husselcommissie of tossleider nodig",
    "Leden hoeven niet terug te lopen naar het clubhuis",
    "Dat bespaart speeltijd voor iedereen",
    "Leden melden zich aan via hun mobiele telefoon",
    "Realtime de indeling zien op je telefoon",
    "Laat vooraf weten dat je komt en zie wie er nog meer komen",
    "Zien wie er zijn aangemeld",
    "Meldingen bij nieuwe ronde",
    "Meldingen of de sessie wel/niet doorgaat",
    "Volledig op afstand te gebruiken",
    "Voor zowel tennis als padel",
  ];

  const features = [
    {
      icon: Shuffle,
      title: "Automatische Indeling",
      description:
        "Slimme algoritmes verdelen spelers eerlijk over de beschikbare banen, rekening houdend met niveau en gespeelde rondes.",
    },
    {
      icon: Smartphone,
      title: "Aanmelden via Mobiel",
      description: "Leden melden zich eenvoudig aan via hun telefoon. Geen pasjes, briefjes of lijsten meer nodig.",
    },
    {
      icon: Clock,
      title: "Realtime Overzicht",
      description:
        "Spelers zien direct op hun telefoon met wie ze spelen en op welke baan. Geen terugloop naar het clubhuis.",
    },
    {
      icon: Smartphone,
      title: "Zie vooraf wie er komt",
      description:
        "Laat met één klik weten dat je komt en zie welke clubgenoten er ook zijn. Zo trek je elkaar over de streep.",
    },
    {
      icon: Bell,
      title: "Push Notificaties",
      description:
        "Automatische meldingen bij nieuwe rondes, wijzigingen of afgelasting. Iedereen weet direct waar ze moeten zijn.",
    },
    {
      icon: Users,
      title: "Geen Vrijwilligers Nodig",
      description: "Geen husselcommissie of husselaar meer nodig die dag. Het systeem draait volledig zelfstandig.",
    },
    {
      icon: Shield,
      title: "Club Branding",
      description:
        "De app toont het logo en de naam van jouw club. Volledig white-label voor een professionele uitstraling.",
    },
  ];

  const manualTasks = [
    {
      title: "Banen reserveren",
      description: "Iemand moet natuurlijk wel banen vrijhouden voor de sessie",
    },
    {
      title: "Sessie aanmaken",
      description: "Een beheerder maakt de hussel-/tossessie aan in het systeem",
    },
    {
      title: "Belsignaal (optioneel)",
      description: "Een belletje of alarm als de ronde voorbij is - tip: alarm op telefoon",
    },
  ];

  const steps = [
    {
      number: "1",
      title: "Registreer je Club",
      description: "Maak een account aan en registreer je tennis- of padelclub in een paar minuten.",
    },
    {
      number: "2",
      title: "Configureer Banen",
      description: "Voeg je banen toe met type, verlichting en indeling die past bij jouw park.",
    },
    {
      number: "3",
      title: "Nodig Leden Uit",
      description: "Deel de link met je leden. Zij maken eenmalig een account aan met hun niveau.",
    },
    {
      number: "4",
      title: "Start met Husselen",
      description: "De app regelt de rest - automatische indelingen, notificaties en meer.",
    },
  ];

  return (
    <div className="min-h-screen bg-background">

      <ContentPageNav showPricing />

      <main>
        {/* Hero / About Section */}
        <section className="py-10 md:py-24 px-3">
          <div className="container mx-auto max-w-6xl">
            <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
              {/* Text */}
              <div>
                <Badge className="mb-4 bg-accent/20 text-accent border-accent/30">Over De Hussel App</Badge>
                <h1 className="text-2xl md:text-4xl font-bold text-foreground mb-6 leading-tight">
                  Waarom ik deze app heb gebouwd
                </h1>
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  <p>
                    Je bent waarschijnlijk op deze pagina gekomen omdat jouw tennisclub husselen, tossen of racket
                    trekken op de agenda heeft staan. Mijn naam is Oscar van Heeswijk (36) en ik speel zelf tennis en
                    padel in Breda. Daar noemen we het husselen. Een commissie met vrijwilligers maakt op een
                    husselmoment de indelingen.
                  </p>
                  <p>
                    Op drukke momenten is dit een hele puzzel voor de indeler om iedereen gelijkwaardig en met allerlei
                    variabelen goed in te delen. Het clubhuis moet open en bij een ronde-wissel stormt iedereen
                    tegelijkertijd op het clubhuis af.
                  </p>
                  <p>
                    Dit zorgt voor drukte en door het op- en neer lopen verlies je soms wel 10 min. speeltijd. Toen dacht
                    ik; dat moet slimmer kunnen. Meer speeltijd voor spelers, en minder druk op een club om vrijwilligers
                    te vinden en in te zetten.
                  </p>
                  <p className="text-foreground font-semibold text-lg">
                    En daar heb ik een oplossing voor bedacht en gebouwd: De Hussel App.
                  </p>
                </div>
              </div>

              {/* Photo */}
              <div className="flex justify-center md:justify-end">
                <div className="relative">
                  <img
                    src={oscarPhoto}
                    alt="Oscar van Heeswijk, oprichter van De Hussel App"
                    className="rounded-2xl shadow-2xl max-h-[500px] w-auto object-cover"
                    loading="lazy"
                  />
                  <div className="absolute -bottom-3 -left-3 bg-accent text-accent-foreground px-4 py-2 rounded-xl text-sm font-semibold shadow-lg">
                    Oscar van Heeswijk
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Logo Section */}
        <section className="py-8 md:py-16 px-3">
          <div className="container mx-auto max-w-6xl flex justify-center">
            <img
              src={husselLogo}
              alt="De Hussel App logo"
              className="h-32 md:h-48 w-auto"
              loading="lazy"
            />
          </div>
        </section>

        {/* Benefits Section (same as landing) */}
        <section className="py-10 md:py-24 px-3">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-8 md:mb-12">
              <h2 className="text-2xl md:text-4xl font-bold text-foreground mb-4">De voordelen van De Hussel App</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Alles wat een husselcommissie doet, volledig geautomatiseerd.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-center gap-3 p-4 bg-card rounded-lg border border-border">
                  <CheckCircle className="h-5 w-5 text-accent flex-shrink-0" />
                  <span className="text-foreground">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <ClubCarousel />

        {/* Features Section */}
        <section className="py-10 md:py-24 px-3 bg-background/90 backdrop-blur-sm">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-8 md:mb-12">
              <h2 className="text-2xl md:text-4xl font-bold text-foreground mb-4">Hoe werkt het?</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Van aanmelden tot indeling - De Hussel App regelt het allemaal automatisch.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((feature, index) => (
                <Card key={index} className="p-4 md:p-6 bg-card border-border hover:border-accent/50 transition-colors">
                  <div className="bg-accent/20 w-12 h-12 rounded-xl flex items-center justify-center mb-4">
                    <feature.icon className="h-6 w-6 text-accent" />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* What Still Needs Manual Work */}
        <section className="py-10 md:py-24 px-3">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-8 md:mb-12">
              <h2 className="text-2xl md:text-4xl font-bold text-foreground mb-4">Wat moet de club nog wel doen?</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Een paar dingen blijven nog nodig - de rest verloopt vanzelf.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 max-w-3xl mx-auto">
              {manualTasks.map((task, index) => (
                <div key={index} className="text-center">
                  <div className="bg-accent text-accent-foreground w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                    {index + 1}
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">{task.title}</h3>
                  <p className="text-muted-foreground text-sm">{task.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How it Works Section */}
        <section className="py-10 md:py-24 px-3 bg-background/90 backdrop-blur-sm">
          <div className="container mx-auto max-w-6xl">
            <Card className="p-5 md:p-12 bg-card border-border">
              <div className="text-center mb-8 md:mb-12">
                <h2 className="text-2xl md:text-4xl font-bold text-foreground mb-4">Aan de slag in 4 stappen</h2>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  In vier eenvoudige stappen is je club klaar om te husselen.
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-8">
                {steps.map((step, index) => (
                  <div key={index} className="text-center">
                    <div className="bg-accent text-accent-foreground w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                      {step.number}
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">{step.title}</h3>
                    <p className="text-muted-foreground text-sm">{step.description}</p>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </section>

        {/* Pricing Section */}
        <section id="pricing" className="py-10 md:py-24 px-3">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-8 md:mb-12">
              <h2 className="text-2xl md:text-4xl font-bold text-foreground mb-4">Prijzen op maat voor jouw club</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Kies het pakket dat past bij de grootte van jouw club. Alle pakketten bevatten dezelfde functies.
              </p>
            </div>

            {/* Billing Interval Toggle */}
            <div className="flex items-center justify-center gap-4 mb-10">
              <span className={`text-sm font-medium ${billingInterval === 'monthly' ? 'text-foreground' : 'text-muted-foreground'}`}>
                Maandelijks
              </span>
              <Switch
                checked={billingInterval === 'yearly'}
                onCheckedChange={(checked) => setBillingInterval(checked ? 'yearly' : 'monthly')}
              />
              <span className={`text-sm font-medium ${billingInterval === 'yearly' ? 'text-foreground' : 'text-muted-foreground'}`}>
                Jaarlijks
              </span>
              {billingInterval === 'yearly' && (
                <Badge className="bg-yellow-300 text-yellow-800 border-yellow-400">
                  Bespaar tot 44%
                </Badge>
              )}
            </div>

            <div className="grid md:grid-cols-3 gap-6 mb-8 md:mb-12">
              {PRICING_TIERS.map((tier) => {
                const displayPrice = billingInterval === 'yearly' ? tier.priceExclBTW : tier.monthlyPriceExclBTW;
                const displayPriceInclBTW = billingInterval === 'yearly' ? tier.priceInclBTW : tier.monthlyPriceInclBTW;
                return (
                <Card
                  key={tier.id}
                  className={`p-6 relative overflow-hidden transition-all ${tier.isPopular ? "border-2 border-primary bg-primary/5 scale-105 shadow-xl" : "bg-card border-border hover:border-primary/50"}`}
                >
                  {tier.isPopular && (
                    <Badge className="absolute top-4 right-4 bg-accent text-accent-foreground">Meest gekozen</Badge>
                  )}
                  {billingInterval === 'yearly' && (
                    <Badge className={`absolute ${tier.isPopular ? 'top-12' : 'top-4'} right-4 bg-yellow-300 text-yellow-800 border-yellow-400 text-xs`}>
                      -{tier.savingsPercent}%
                    </Badge>
                  )}
                  <div className="mb-6">
                    <h3 className="text-xl font-bold text-foreground mb-1">{tier.name}</h3>
                    <p className="text-muted-foreground text-sm">{tier.memberRange}</p>
                  </div>
                  <div className="mb-6">
                    {billingInterval === 'yearly' && (
                      <p className="text-sm text-muted-foreground line-through mb-1">
                        €{formatPrice(tier.monthlyPriceExclBTW)}/maand
                      </p>
                    )}
                    <div className="flex items-baseline gap-1">
                      <span className="text-lg text-muted-foreground">€</span>
                      <span className={`text-4xl font-bold ${tier.isPopular ? "text-accent" : "text-foreground"}`}>
                        {formatPrice(displayPrice)}
                      </span>
                      <span className="text-muted-foreground">/maand</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      excl. 21% BTW (€{formatPrice(displayPriceInclBTW)} incl.)
                    </p>
                    {billingInterval === 'yearly' && (
                      <p className="text-xs text-yellow-700 dark:text-yellow-400 mt-1 font-medium">
                        €{formatPrice(tier.yearlyTotalExclBTW)}/jaar · Bespaar €{formatPrice(tier.savingsTotal)}
                      </p>
                    )}
                  </div>
                  <ul className="space-y-2 mb-6">
                    {[
                      "Onbeperkt aantal sessies",
                      "Automatische indelingen",
                      "Push notificaties",
                      "Club branding",
                      "Live dashboard",
                      "Banen beheer",
                      "Meldingen beheer",
                    ].map((feature, index) => (
                      <li key={index} className="flex items-center gap-2 text-sm text-foreground">
                        <CheckCircle className="h-4 w-4 text-accent flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    size="lg"
                    className={`w-full gap-2 ${tier.isPopular ? "bg-accent hover:bg-accent/90 text-accent-foreground" : ""}`}
                    variant={tier.isPopular ? "default" : "outline"}
                    onClick={() => navigate("/club-register")}
                  >
                    <Building2 className="h-5 w-5" />
                    Kies {tier.name.toLowerCase()}
                  </Button>
                </Card>
                );
              })}
            </div>

            <Card className="p-4 md:p-8 bg-card border-border">
              <h3 className="text-xl font-bold text-foreground mb-4 text-center">Kosten per lid per maand</h3>
              <p className="text-muted-foreground text-center mb-6">
                Zie hoe voordelig De Hussel App is, ongeacht de grootte van je club
              </p>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Pakket</TableHead>
                      <TableHead className="text-center">Scenario 1</TableHead>
                      <TableHead className="text-center">Scenario 2</TableHead>
                      <TableHead className="text-center">Scenario 3</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {PRICING_TIERS.map((tier) => {
                      const examples = getExampleCostsPerMember(tier.id as PricingTier, billingInterval);
                      const displayPrice = billingInterval === 'yearly' ? tier.priceExclBTW : tier.monthlyPriceExclBTW;
                      return (
                        <TableRow key={tier.id} className={tier.isPopular ? "bg-accent/10" : ""}>
                          <TableCell className="font-medium">
                            <div>
                              <p className="font-semibold">{tier.name}</p>
                              <p className="text-xs text-muted-foreground">€{formatPrice(displayPrice)}/mnd</p>
                            </div>
                          </TableCell>
                          {examples.map((example, i) => (
                            <TableCell key={i} className="text-center">
                              <p className="font-semibold text-accent">€{example.costPerMember}</p>
                              <p className="text-xs text-muted-foreground">bij {example.members} leden</p>
                            </TableCell>
                          ))}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              <div className="text-center mt-6 space-y-3">
                <p className="text-sm text-muted-foreground">
                  {billingInterval === 'yearly'
                    ? 'Jaarabonnement: 12 maanden, maandelijks opzegbaar na het eerste jaar. Alle prijzen excl. 21% BTW.'
                    : 'Maand-Flex: maandelijks opzegbaar. Alle prijzen excl. 21% BTW.'}
                </p>
                <div className="flex items-center justify-center gap-3 pt-2">
                  <span className="text-xs text-muted-foreground">Betaalmethoden:</span>
                  <div className="flex items-center gap-2">
                    <img src="https://www.mollie.com/external/icons/payment-methods/ideal.svg" alt="iDEAL" className="h-6" width={32} height={24} />
                    <img src="https://www.mollie.com/external/icons/payment-methods/bancontact.svg" alt="Bancontact" className="h-6" width={32} height={24} />
                    <img src="https://www.mollie.com/external/icons/payment-methods/directdebit.svg" alt="SEPA Incasso" className="h-6" width={32} height={24} />
                    <img src="https://www.mollie.com/external/icons/payment-methods/creditcard.svg" alt="Creditcard" className="h-6" width={32} height={24} />
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-10 md:py-24 px-3">
          <div className="container mx-auto max-w-4xl">
            <Card className="p-5 md:p-12 bg-accent/10 border-accent/30 text-center">
              <h2 className="text-2xl md:text-4xl font-bold text-foreground mb-4">Klaar om te starten?</h2>
              <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                Sluit je aan bij de tennis- en padelclubs die hun husselsessies al efficiënter organiseren met De Hussel App.
              </p>
              <Button
                size="lg"
                className="gap-2 bg-accent hover:bg-accent/90 text-accent-foreground"
                onClick={() => navigate("/club-register")}
              >
                Registreer je Club
                <ArrowRight className="h-5 w-5" />
              </Button>
            </Card>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-border">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center">
              <img src={husselLogo} alt="De Hussel App" className="h-12 w-12 object-contain" width={48} height={48} />
            </div>
            <div className="flex flex-col items-center md:items-end gap-2">
              <p className="text-sm text-muted-foreground">
                © {new Date().getFullYear()} De Hussel App. Alle rechten voorbehouden.
              </p>
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <a href="/algemene-voorwaarden" className="hover:text-primary transition-colors">Algemene Voorwaarden</a>
                <span>•</span>
                <a href="/privacy-website" className="hover:text-primary transition-colors">Privacybeleid</a>
                <span>•</span>
                <a href="/contact" className="hover:text-primary transition-colors">Contact</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default OverDeHusselApp;
